import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AccordionDetails,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { Pagination, ReceivingArea, ReceivingAreaProfileMapping, SupplyProfile } from '@ekkogmbh/apisdk';
import { Add, Stop } from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import useRequest from 'src/Common/Hooks/useRequest';
import PaginatedSelect from 'src/Common/Components/Forms/PaginatedSelect';
import usePagination from 'src/Common/Providers/PaginationProvider';
import StyledFormAccordion from 'src/Common/Components/Forms/StyledFormAccordion';

interface Props {
  supplyProfile?: SupplyProfile;
  closeHandler: () => void;
}

export const SupplyProfilePanel = ({ supplyProfile, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { enableSave } = usePanel();
  const { refresh } = usePagination();

  const [, startLoading] = useRequest();
  const [popoverState, setPopoverState] = useState<{
    type: 'field' | 'color';
    anchor: HTMLButtonElement;
    coordinate: string;
    state?: string;
    inputValue?: string;
  }>();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [receivingAreaMappings, setReceivingAreaMappings] = useState<ReceivingAreaProfileMapping[]>([]);

  const receivingAreas = useMemo(() => receivingAreaMappings.map((ra) => ra.receivingArea), [receivingAreaMappings]);
  // re-indexing by coordinate to avoid searches
  const areaMappings = useMemo(
    () =>
      receivingAreaMappings.reduce(
        (carry, mapping) => {
          carry[mapping.receivingArea.coordinate] = mapping;
          return carry;
        },
        {} as Record<string, ReceivingAreaProfileMapping>,
      ),
    [receivingAreaMappings],
  );

  const isEditMode = useMemo(() => supplyProfile !== undefined, [supplyProfile]);

  const savePayload = useMemo(() => {
    if ([name, coordinate].includes('') && receivingAreaMappings.length > 0) {
      return undefined;
    }

    return {
      name,
      coordinate,
      receivingAreaMappings: Object.values(areaMappings),
    } as SupplyProfile;
  }, [name, coordinate, areaMappings, receivingAreaMappings]);

  useEffect(() => {
    enableSave(savePayload !== undefined);
  }, [savePayload]);

  const getMappingIndex = useCallback(
    (_coordinate: string) => receivingAreaMappings.findIndex((ra) => ra.receivingArea.coordinate === _coordinate),
    [receivingAreaMappings],
  );

  const save = useCallback(() => {
    if (savePayload === undefined) {
      return;
    }

    startLoading(
      () =>
        api.saveSupplyProfile(
          {
            name: savePayload.name,
            coordinate: savePayload.coordinate,
          },
          {
            receivingAreas: Object.values(areaMappings).map((mapping: ReceivingAreaProfileMapping) => {
              const { observedStates, observedFields, stateColors, receivingArea } = mapping;
              const { name, coordinate } = receivingArea;

              // remove colors for states that are not observed
              Object.keys(stateColors).forEach((state: string) => {
                if (!observedStates.includes(state)) {
                  delete stateColors[state];
                }
              });

              return { name, coordinate, observedStates, observedFields, stateColors };
            }),
          },
        ),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [api, savePayload, closeHandler]);

  const reset = useCallback(() => {
    setName(supplyProfile?.name ?? '');
    setCoordinate(supplyProfile?.coordinate ?? '');
    setReceivingAreaMappings(supplyProfile?.receivingAreaMappings ?? []);
  }, [supplyProfile]);

  useEffect(() => reset(), [reset]);

  const fetchAreas = useCallback(
    (pagination: Pagination) =>
      api.getReceivingAreas({
        ...pagination,
        filter: {
          coordinate,
        },
      }),
    [api, coordinate],
  );

  const popover = useMemo(() => {
    if (popoverState === undefined) {
      return <></>;
    }

    let form: React.JSX.Element;
    switch (popoverState.type) {
      case 'color': {
        form = (
          <Stack spacing={2} sx={{ p: 2 }}>
            <HexColorPicker
              color={popoverState.inputValue}
              onChange={(color) => {
                setPopoverState({
                  ...popoverState,
                  inputValue: color,
                });
              }}
            />
            <StyledTextField
              label="Hex Color"
              value={popoverState.inputValue ?? ''}
              onChange={(e) => {
                setPopoverState({
                  ...popoverState,
                  inputValue: e.target.value,
                });
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (popoverState.state === undefined || popoverState.inputValue === undefined) {
                  return;
                }
                const idx = getMappingIndex(popoverState.coordinate);
                receivingAreaMappings[idx].stateColors[popoverState.state] = popoverState.inputValue;
                setReceivingAreaMappings([...receivingAreaMappings]);
                setPopoverState(undefined);
              }}
            >
              Confirm
            </Button>
          </Stack>
        );
        break;
      }
      case 'field': {
        form = (
          <Stack spacing={2} sx={{ p: 2 }}>
            <StyledTextField
              label="Field Name"
              value={popoverState.inputValue ?? ''}
              onChange={(e) => {
                setPopoverState({
                  ...popoverState,
                  inputValue: String(e.target.value),
                });
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const idx = getMappingIndex(popoverState.coordinate);
                if (
                  popoverState.inputValue === undefined ||
                  popoverState.inputValue === '' ||
                  receivingAreaMappings[idx].observedFields.includes(popoverState.inputValue)
                ) {
                  return;
                }

                receivingAreaMappings[idx].observedFields.push(popoverState.inputValue);
                setReceivingAreaMappings([...receivingAreaMappings]);
                setPopoverState(undefined);
              }}
            >
              Confirm
            </Button>
          </Stack>
        );
      }
    }

    return (
      <Popover
        open
        anchorEl={popoverState.anchor}
        anchorOrigin={{ horizontal: 'left', vertical: 'center' }}
        transformOrigin={{ horizontal: 'center', vertical: 'center' }}
        onClose={() => setPopoverState(undefined)}
      >
        {form}
      </Popover>
    );
  }, [popoverState, receivingAreaMappings]);

  const removeField = useCallback(
    (coordinate: string, field: string) => {
      const idx = getMappingIndex(coordinate);
      const fieldIndex = receivingAreaMappings[idx].observedFields.indexOf(field);

      if (fieldIndex >= 0) {
        receivingAreaMappings[idx].observedFields.splice(fieldIndex, 1);
        setReceivingAreaMappings([...receivingAreaMappings]);
      }
    },
    [receivingAreaMappings],
  );

  const toggleObservedState = useCallback(
    (mappingIdx: number, state: string) => {
      const stateIndex = receivingAreaMappings[mappingIdx].observedStates.indexOf(state);
      if (stateIndex < 0) {
        receivingAreaMappings[mappingIdx].observedStates.push(state);
      } else {
        receivingAreaMappings[mappingIdx].observedStates.splice(stateIndex, 1);
      }
      setReceivingAreaMappings([...receivingAreaMappings]);
    },
    [receivingAreaMappings],
  );

  const mappingAccordion = useCallback(
    (mappingIdx: number): React.JSX.Element => {
      const mapping = receivingAreaMappings[mappingIdx];
      const {
        receivingArea: { name, coordinate },
      } = mapping;

      return (
        <StyledFormAccordion label={name} centerLabel={coordinate}>
          <AccordionDetails>
            <Stack spacing={2}>
              <Tooltip arrow title={'select states to appear in the dashboard and in what colour'} placement={'left'}>
                <Typography variant={'overline'}>Monitored States</Typography>
              </Tooltip>
              <Stack spacing={1}>
                {mapping.receivingArea.replenishmentPlan.linearStateMachineDefinition.states.map((state) => {
                  const isObserved = mapping.observedStates.includes(state);
                  const color = isObserved ? mapping.stateColors[state] : undefined;
                  const colorIcon = (
                    <IconButton
                      disabled={!isObserved}
                      onClick={(event) =>
                        setPopoverState({
                          anchor: event.currentTarget,
                          coordinate,
                          state,
                          inputValue: color,
                          type: 'color',
                        })
                      }
                    >
                      <Stop style={{ color }} />
                    </IconButton>
                  );
                  return (
                    <Box key={`${state}-state-select-key`} sx={{ border: 1, borderRadius: 1 }}>
                      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Checkbox
                          checked={mapping.observedStates.includes(state)}
                          onClick={() => toggleObservedState(mappingIdx, state)}
                        />
                        <Typography>{state}</Typography>
                        {colorIcon}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
              <Tooltip arrow title={'compartment fields for the table component of the dashboard'} placement={'left'}>
                <Typography variant={'overline'}>Displayed Compartment Fields</Typography>
              </Tooltip>
              <Stack direction={'row'} spacing={1} alignItems={'center'} flexWrap={'wrap'} useFlexGap>
                <IconButton
                  onClick={(event) => {
                    setPopoverState({
                      type: 'field',
                      anchor: event.currentTarget,
                      coordinate,
                      inputValue: '',
                    });
                  }}
                >
                  <Add />
                </IconButton>
                {mapping.observedFields.map((field: string, idx: number) => (
                  <Tooltip key={idx} title={field} placement={'top'}>
                    <Chip
                      variant={'outlined'}
                      label={
                        <Typography
                          sx={{
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {field}
                        </Typography>
                      }
                      onDelete={() => removeField(coordinate, field)}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Stack>
          </AccordionDetails>
        </StyledFormAccordion>
      );
    },
    [receivingAreaMappings],
  );

  return (
    <ContentPanel
      steps={[
        {
          title: 'Supply Profile',
          contentCallback: () => (
            <Stack spacing={2} alignItems={'center'}>
              {popover}
              <StyledFormHeader label={'Identifier'} />
              <Stack spacing={2} sx={{ width: { xs: '100%', lg: '60%' } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box flex={1}>
                    <StyledTextField
                      type={'text'}
                      label={'Name'}
                      value={name}
                      disabled={isEditMode}
                      onChange={(e) => setName(String(e.target.value))}
                    />
                  </Box>
                  <Box flex={1}>
                    <CoordinateInput
                      value={coordinate}
                      disabled={isEditMode || receivingAreas.length > 0}
                      onChange={(coordinate) => setCoordinate(coordinate)}
                      trailingDelimiter={false}
                    />
                  </Box>
                </Stack>
                <PaginatedSelect
                  multiple
                  multipleVariant={'chip'}
                  label={'Receiving Areas'}
                  value={receivingAreas}
                  fetchPaginatedItems={fetchAreas}
                  itemId={(ra) => ra.coordinate}
                  itemRender={(ra) => ra.name}
                  onChange={(ras) => {
                    if (ras === undefined) {
                      return;
                    }
                    const coordinates = Object.keys(areaMappings);
                    const mappingsUpdate: ReceivingAreaProfileMapping[] = [];
                    for (const ra of ras as ReceivingArea[]) {
                      // Create new mapping for new entries, copy exisitng
                      const isNew = !coordinates.includes(ra.coordinate);
                      if (isNew) {
                        mappingsUpdate.push({
                          receivingArea: ra,
                          observedFields: [],
                          observedStates: [],
                          stateColors: {},
                        });
                      } else {
                        mappingsUpdate.push(areaMappings[ra.coordinate]);
                      }
                    }
                    setReceivingAreaMappings(mappingsUpdate);
                  }}
                />
              </Stack>
              {receivingAreas.length > 0 && (
                <Stack spacing={2} sx={{ width: { xs: '100%', lg: '60%' } }}>
                  <StyledFormHeader label={'Configuration'} />
                  <Stack spacing={1}>
                    {Object.keys(areaMappings).map((coord: string) => (
                      <Box key={coord}>{mappingAccordion(getMappingIndex(coord))}</Box>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          ),
        },
      ]}
      onSave={save}
      onCancel={closeHandler}
      onReset={reset}
    />
  );
};
